import React, {
  useRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useContext,
} from 'react';
import uniqueId from 'lodash/uniqueId';
import cn from 'classnames';
import ListHeader from '../../../List/ListHeader';
import CheckIcon from '../../../Icons/24x24/Check';
import { IconSize } from '../../../Icons/IconWrap';
import {
  ThemeOverride,
  ThemeOptions,
} from '@wix/da-react-context/pkg/ThemeContext';
import { PopperDropdownManager } from '../../PopperDropdownLayout';
import { type DropdownItem, DropdownOption } from '../../types';
import { getSelectedItemByValue, findItemByValue } from '../../helpers';
import StandardDropdownMenu from '../StandardDropdown/StandardDropdownMenu';
import { DropdownStateContext } from '../../DropdownStateManager';

import s from './SelectionListItemDropdown.scss';

export interface Props {
  items: DropdownItem[];
  renderToggle: (id: string, selectedItem?: DropdownOption) => React.ReactNode;
  selectedValue?: string | number;
  className?: string;
  menuClassName?: string;
  menuLabel?: React.ReactNode;
  menuRef?: React.RefCallback<HTMLDivElement>;
  highlightActive?: boolean;
  isInPopup?: boolean;
  usePortal?: boolean;
  forceTheme?: ThemeOptions;
  onSelect?: (value, label?) => void;
}

const ListItemDropdown: React.FC<React.PropsWithChildren<Props>> = ({
  items,
  renderToggle,
  selectedValue,
  className,
  menuClassName,
  menuLabel,
  menuRef,
  highlightActive = true,
  isInPopup,
  usePortal,
  forceTheme = ThemeOptions.LIGHT,
  onSelect,
}) => {
  const { setMenuRef } = useContext(DropdownStateContext);
  const [selectedItem, setSelectedItem] = useState<DropdownOption | undefined>(
    findItemByValue(selectedValue, items)
  );

  const { current: dropdownId } = useRef(uniqueId('dropdown'));

  const dropdownItems: DropdownItem[] = useMemo(
    () =>
      items.map(item => ({
        ...item,
        suffix:
          highlightActive &&
          selectedItem?.value === (item as DropdownOption).value ? (
            <CheckIcon
              size={IconSize.MEDIUM}
              className={s['check-icon']}
              disableDefaultColors
            />
          ) : undefined,
      })),
    [items, highlightActive, selectedItem]
  );

  const onMenuSelect = useCallback(
    (value: string | number | undefined, label: any) => {
      const selected = getSelectedItemByValue(value, items);
      if (selected?.disabled) {
        return;
      }
      setSelectedItem(selected);
      onSelect && onSelect(value, label);
    },
    [items, onSelect]
  );

  // Update selected state if it's undefined and `selectedValue` prop changes
  useEffect(() => {
    if (selectedValue !== undefined) {
      if (!selectedItem) {
        setSelectedItem(findItemByValue(selectedValue, items));
      } else {
        // verify that selected item still exists
        if (findItemByValue(selectedItem.value, items)) {
          setSelectedItem(findItemByValue(selectedValue, items));
        }
      }
    }
  }, [selectedItem, selectedValue, items]);

  const menuRefHandler = node => {
    // Set reference on dropdown state - this is required for dropdown to work properly
    setMenuRef(node);
    // Set reference on external prop - useful if we need a ref to the menu outside this component
    menuRef && menuRef(node);
  };

  return (
    <PopperDropdownManager isInPopup={isInPopup}>
      {renderToggle(dropdownId, selectedItem)}
      <StandardDropdownMenu
        id={dropdownId}
        role="listbox"
        menuInnerRef={menuRefHandler}
        menuLabel={
          menuLabel ? (
            <ThemeOverride
              theme={ThemeOptions.LIGHT}
              className={s['list-header']}
            >
              <ListHeader withDivider>{menuLabel}</ListHeader>
            </ThemeOverride>
          ) : undefined
        }
        menuLabelClassName={s['menu-label']}
        items={dropdownItems}
        itemClassName={s['dropdown-item']}
        menuClassName={cn(s['menu'], menuClassName)}
        className={className}
        withHoverEffect={false}
        withMenuPaddings={false}
        hideCheckIcon
        onSelect={onMenuSelect}
        usePortal={usePortal}
      />
    </PopperDropdownManager>
  );
};
ListItemDropdown.displayName = 'ListItemDropdown';

export default ListItemDropdown;
